import React, { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useProducts } from '../../context/ProductContext';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';

function CartItem({ item }) {
  const { updateCartItem, updateQuantity, removeFromCart } = useCart();
  const { formatPrice } = useCurrency();
  const { products } = useProducts();
  const { primaryColor } = useTheme();
  const [error, setError] = useState('');
  const product = products.find((p) => p.id === item.id);

  // Prevent rendering if item is from non-cart-eligible category
  if (!product || ['real_estate', 'cars'].includes(product.category)) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-md mb-4 text-center">
        <p className="text-lg font-montserrat text-red-600">
          Invalid item: {item.name} cannot be added to cart.
        </p>
        <Button
          onClick={() => removeFromCart(item.variantKey)}
          className="mt-2 bg-red-600 text-cream px-4 py-2 text-sm font-montserrat rounded-full hover:bg-red-700"
          aria-label={`Remove invalid item ${item.name} from cart`}
        >
          Remove
        </Button>
      </div>
    );
  }

  const handleUpdate = (updates) => {
    if (updates.selectedSize && !product.sizes?.includes(updates.selectedSize)) {
      setError('Invalid size selected.');
      return;
    }
    if (updates.selectedColor && !product.colors?.includes(updates.selectedColor)) {
      setError('Invalid color selected.');
      return;
    }
    setError('');
    updateCartItem(item.variantKey, updates);
  };

const handleQuantityChange = (delta) => {
  const newQuantity = item.quantity + delta;

  if (newQuantity < 1) {
    setError('Quantity cannot be less than 1.');
    return;
  }

  // Only enforce stock limit if the product is actually stock-tracked
  if (product.has_stock && newQuantity > product.stock) {
    setError(`Only ${product.stock} items in stock.`);
    return;
  }

  setError('');
  updateQuantity(item.variantKey, newQuantity);
};

  const handleRemove = () => {
    if (window.confirm(`Are you sure you want to remove ${item.name} from your cart?`)) {
      removeFromCart(item.variantKey);
    }
  };

  const getImageSrc = () => {
    if (product.category === 'books' && product.imageViews) {
      return process.env.REACT_APP_PRODUCT_URL+""+product.image || '/assets/images/placeholder.jpg';
    }
    if ((product.category === 'clothing' || product.category === 'shoes') && item.selectedColor) {
      return process.env.REACT_APP_PRODUCT_URL+""+product.image || '/assets/images/placeholder.jpg';
    }
    return process.env.REACT_APP_PRODUCT_URL+""+product.image || '/assets/images/placeholder.jpg';
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white p-4 rounded-lg shadow-md mb-4">
      <img
        src={getImageSrc()}
        alt={`${item.name} ${item.selectedColor || ''}`}
        className="w-24 h-24 object-cover rounded-md"
        onError={() => console.error(`Failed to load image for ${item.name}`)}
      />
      <div className="flex-1 space-y-2">
        <h2 className="text-xl font-playfair-display text-primary">{item.name}</h2>
        <p className="text-lg font-montserrat text-secondary">
          {formatPrice ? formatPrice(item.price) : `$${item.price.toFixed(2)}`} x {item.quantity} ={' '}
          {formatPrice ? formatPrice(item.price * item.quantity) : `$${(item.price * item.quantity).toFixed(2)}`}
        </p>
        <p className="text-sm font-montserrat text-primary">
  {product.has_stock ? (
    product.stock > 0 ? (
      <span className="text-green-400">Stock: In Stock ({product.stock})</span>
    ) : (
      <span className="text-red-400">Stock: Out of Stock</span>
    )
  ) : (
    <span className="text-gray-500"></span>  // or just leave empty / "Available"
  )}
</p>
        {error && <p className="text-sm font-montserrat text-red-600">{error}</p>}
        <div className="flex gap-4 flex-wrap">
          {/* Size Selection for Clothing and Shoes */}
          {(product.category === 'clothing' || product.category === 'shoes') && product.sizes?.length > 0 && (
            <div>
              <label className="text-sm font-montserrat text-secondary block">Size</label>
              <select
                value={item.selectedSize || ''}
                onChange={(e) => handleUpdate({ selectedSize: e.target.value })}
                className="w-24 p-1 border border-secondary rounded-md text-montserrat text-secondary bg-cream focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={`Select size for ${item.name}`}
                style={{ '--primary': primaryColor }}
              >
                <option value="" disabled>
                  Select size
                </option>
                {product.sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Color Selection for Clothing and Shoes */}
          {(product.category === 'clothing' || product.category === 'shoes') && product.colors?.length > 0 && (
            <div>
              <label className="text-sm font-montserrat text-secondary block">Color</label>
              <select
                value={item.selectedColor || ''}
                onChange={(e) => handleUpdate({ selectedColor: e.target.value })}
                className="w-24 p-1 border border-secondary rounded-md text-montserrat text-primary bg-cream focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={`Select color for ${item.name}`}
                style={{ '--primary': primaryColor }}
              >
                <option value="" disabled>
                  Select color
                </option>
                {product.colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Format for Books */}
          {product.category === 'books' && product.format?.length > 0 && (
            <div>
              <label className="text-sm font-montserrat text-secondary block">Format</label>
              <p className="text-sm font-montserrat text-secondary">{item.format || product.format[0]}</p>
            </div>
          )}
          {/* Quantity Controls */}
<div>
  <label className="text-sm font-montserrat text-secondary block">Quantity</label>
  <div className="flex items-center gap-2">
    <Button
      onClick={() => handleQuantityChange(-1)}
      className="disabled:opacity-50"
      disabled={item.quantity <= 1}
      aria-label={`Decrease quantity of ${item.name}`}
      aria-disabled={item.quantity <= 1}
      style={{ '--primary': primaryColor }}
    >
      –
    </Button>

    <span className="text-lg font-montserrat text-secondary">{item.quantity}</span>

    <Button
      onClick={() => handleQuantityChange(1)}
      // ──── KEY FIX ────
      disabled={product.has_stock && item.quantity >= product.stock}
      // ─────────────────
      aria-label={`Increase quantity of ${item.name}`}
      aria-disabled={product.has_stock && item.quantity >= product.stock}
      style={{ '--primary': primaryColor }}
    >
      +
    </Button>
  </div>

  {/* Optional: small hint when unlimited */}
  {!product.has_stock && item.quantity >= 10 && (
    <p className="text-xs text-gray-500 mt-1">
      No stock limit — order as many as needed
    </p>
  )}
</div>
        </div>
      </div>
      <Button
        onClick={handleRemove}
        className="bg-red-600 text-cream px-4 py-2 text-sm font-montserrat rounded-full hover:bg-red-700"
        aria-label={`Remove ${item.name} from cart`}
      >
        Remove
      </Button>
    </div>
  );
}

export default CartItem;